import {
  NegativeColors,
  PositiveColors,
  PrimaryColors,
  TextColors,
} from '@picsart/design-system/foundation/colors';
import { FontSizes, LineHeights } from '@picsart/design-system/foundation/typography';

import { createUseStyles } from 'react-jss';

import { canHover, canTouch } from '@picsart/design-system/foundation/styleHelpers';

// import { BreakPoints } from '@picsart/design-system/foundation/responsive';

import { Easing } from '@picsart/design-system/foundation/easing';
import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { Timings } from '@picsart/design-system/foundation/timings';

const useStyles = createUseStyles(
  () => ({
    root: {},
    separation: {
      display: 'flex',
      marginTop: Spacings.s48,
      justifyContent: 'space-between',
      flexWrap: 'wrap',
    },
    separationSide: {
      // [mediaQuery(BreakPoints.Small)]: {
      '@media screen and (max-width: 766px)': {
        marginBottom: Spacings.s0,
      },
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      // [mediaQuery(BreakPoints.Small)]: {
      '@media screen and (max-width: 766px)': {
        width: '100%',
        marginBottom: Spacings.s24,
      },
    },
    actionHolder: {
      marginRight: Spacings.s12,
      // [mediaQuery(766)]: {
      '@media screen and (max-width: 766px)': {
        flex: '1 1 0',
      },

      '&:last-child': {
        margin: Spacings.s0,
      },
    },
    action: {
      position: 'relative',
      display: 'block',
      padding: [Spacings.s0, Spacings.s16],
      borderRadius: CornerRadius.r8,
      border: 'none',
      height: 44,
      outline: 'none',
      backgroundColor: 'transparent',
      fontWeight: FontWeights.semibold,
      fontSize: FontSizes.s16,
      textAlign: 'center',
      textTransform: 'capitalize',
      lineHeight: LineHeights.l48,
      color: TextColors.tint1,
      cursor: 'pointer',

      '&:before': {
        borderColor: NegativeColors.base.default,
        borderRadius: CornerRadius.r12,
        content: '""',
      },

      [canHover]: {
        '&:hover': {
          backgroundColor: NegativeColors.base.default,
          color: NegativeColors.text.default,
        },
      },

      [canTouch]: {
        '&:active': {
          backgroundColor: NegativeColors.base.default,
          color: NegativeColors.text.default,
        },
      },

      '&:active': {
        '&:before': {
          inset: -4,
          zIndex: 'initial',
          opacity: 1,
        },
      },

      // [mediaQuery(766)]: {
      '@media screen and (max-width: 766px)': {
        width: '100%',
      },
    },
    active: {
      backgroundColor: NegativeColors.base.default,
      color: PositiveColors.text.default,
    },
    tabs: {
      display: 'flex',
      alignItems: 'center',
    },
    tabHolder: {
      marginRight: Spacings.s8,

      '&:last-child': {
        margin: Spacings.s0,
      },
    },
    tab: {
      position: 'relative',
      display: 'block',
      padding: [Spacings.s0, Spacings.s16],
      height: 44,
      borderRadius: CornerRadius.r8,
      border: 'none',
      outline: 'none',
      backgroundColor: 'transparent',
      fontWeight: FontWeights.semibold,
      fontSize: FontSizes.s16,
      textAlign: 'center',
      textTransform: 'capitalize',
      lineHeight: LineHeights.l48,
      color: TextColors.tint3,
      transition: `color ${Timings.default} ${Easing.easeInOut}`,
      cursor: 'pointer',

      '&:before': {
        position: 'absolute',
        bottom: Spacings.s0,
        left: Spacings.s0,
        right: Spacings.s0,
        display: 'inline-block',
        margin: [0, 'auto'],
        width: 24,
        height: 2,
        content: '""',
        backgroundColor: PrimaryColors.base.default,
        transform: 'scale(1, 0)',
        transformOrigin: 'bottom center',
        transition: `transform ${Timings.default} ${Easing.easeInOut}`,
      },

      [canHover]: {
        '&:hover': {
          color: TextColors.base,
        },
      },

      [canTouch]: {
        '&:active': {
          color: TextColors.base,
        },
      },
    },
    activeTab: {
      color: TextColors.base,
      '&:before': {
        transform: 'scale(1, 1)',
      },
    },
    gridHolder: {
      marginTop: Spacings.s40,
    },
    paginationHolder: {
      marginTop: Spacings.s64,
      paddingBottom: 120,
    },
    skeletonGridHolder: {
      extend: 'gridHolder',
      marginTop: Spacings.s0,
    },
    skeletonRoot: {
      extend: 'root',
      gridTemplateColumns: '200px 200px 200px 200px',
    },
    skeletonActions: {
      extend: 'actions',
      columnGap: CornerRadius.r8,
      marginBottom: Spacings.s48,
      // [mediaQuery(767)]: {
      '@media screen and (max-width: 767px)': {
        marginBottom: Spacings.s4,
        width: '100%',
      },
      '& > i': {
        width: '100%',
        minWidth: 120,
        height: 48,
      },
    },
    skeletonAction: {
      extend: 'action',
      width: 140,
      padding: 0,
      // [mediaQuery(767)]: {
      '@media screen and (max-width: 767px)': {
        '& > i': {
          width: 120,
          height: 30,
        },
      },
    },
    skeletonActionHolder: {
      extend: 'actionHolder',
      marginRight: 0,
    },
    skeletonPaginationHolder: {
      extend: 'paginationHolder',
      display: 'grid',
      gridTemplateColumns: '300px 100px',
      width: '100%',
      justifyItems: 'start',
      columnGap: Spacings.s40,
      marginTop: Spacings.s24,
      '& > i': {
        width: 200,
        height: 48,
      },
      // [mediaQuery(767)]: {
      '@media screen and (max-width: 767px)': {
        gridTemplateColumns: '1fr 1fr',
        marginTop: Spacings.s24,
        '& > i': {
          width: 120,
          height: 30,
        },
      },
    },
    skeletonGridItem: {
      height: 200,
    },
  }),
  {
    name: 'list-with-pagination-and-cta',
  },
);

export default useStyles;
